import React, { PropsWithChildren, ReactNode } from 'react';
import styled, { css } from 'styled-components';

import { INPUT_LAYOUT, InputType } from '../../../shared/constants';

import mq from '../../../styles/breakpoints';
import Spacings from '../../../styles/Spacings';

interface Props extends PropsWithChildren {
  layout?: string;
  fieldsetType?: string;
  className?: string;
  fitContainer?: boolean;
}

const FieldWrapper = ({
  children,
  layout,
  fieldsetType,
  className,
  fitContainer,
}: Props) => {
  return (
    <Container
      className={className}
      layout={layout}
      fieldsetType={fieldsetType}
      amountOfItems={children?.length}
      fullWidth={fitContainer}
    >
      {children}
    </Container>
  );
};

export default FieldWrapper;

const GridInputContainer = css<{
  layout?: string;
  fieldsetType: string;
  amountOfItems: number;
  fullWidth?: boolean;
}>`
  grid-gap: ${Spacings.XXS};

  ${({ fullWidth }: { fullWidth?: boolean }) =>
    fullWidth
      ? css`
          display: flex;
          flex-direction: row;
          justify-content: space-between;
        `
      : css`
          display: grid;
          grid-template-columns: 1fr;
        `}

  ${mq.from.breakpoint.M`
    ${({ layout, fullWidth }: { layout?: string; fullWidth?: boolean }) =>
      layout === INPUT_LAYOUT.inline && !fullWidth
        ? css`
            display: grid;
            grid-template-columns: 1fr 1fr 1fr;
          `
        : fullWidth
        ? css`
            display: flex;
            flex-direction: row;
          `
        : css`
            display: grid;
            grid-template-columns: 1fr;
          `}
  `}
`;

const FlexInputContainer = css<{
  layout?: string;
  fieldsetType: string;
  amountOfItems: number;
}>`
  column-gap: ${Spacings.S};
  display: flex;
  flex-direction: column;
  padding-left: 0;
  row-gap: ${Spacings.XXS};

  ${mq.from.breakpoint.M`
    ${({ layout }: { layout?: string }) =>
      layout === INPUT_LAYOUT.inline && 'flex-direction: row; flex-wrap: wrap;'}
  `}
`;

const GridSmallInputContainer = css<{
  layout?: string;
  fieldsetType: string;
  amountOfItems: number;
}>`
  column-gap: ${Spacings.S};
  display: flex;
  flex-direction: column;
  padding-left: 0;
  row-gap: ${Spacings.XXS};

  ${mq.from.breakpoint.M`
    ${({ amountOfItems, layout }: { amountOfItems: number; layout?: string }) =>
      layout === INPUT_LAYOUT.inline &&
      amountOfItems >= 2 &&
      css`
        align-items: baseline;
        column-gap: ${Spacings.S};
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        row-gap: ${Spacings.S};
      `}
  `}

  ${mq.from.breakpoint.L`
    ${({ amountOfItems, layout }: { amountOfItems: number; layout?: string }) =>
      layout === INPUT_LAYOUT.inline &&
      amountOfItems >= 3 &&
      css`
        align-items: baseline;
        column-gap: ${Spacings.S};
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        row-gap: ${Spacings.S};
      `}
  `}
`;

const TextInputContainer = css<{
  layout?: string;
  fieldsetType: string;
  amountOfItems: number;
}>`
  display: grid;
  grid-column-gap: ${Spacings.S};
  grid-row-gap: ${Spacings.S};
  grid-template-columns: repeat(1, 1fr);
  width: 100%;

  ${mq.from.breakpoint.M`
    max-width: 400px;

    ${({ amountOfItems }: { amountOfItems: number }) =>
      amountOfItems < 3
        ? css`
            grid-template-columns: 1fr 1fr;
          `
        : css`
            align-items: baseline;
            grid-template-columns: repeat(2, 96px) auto;
          `};

      ${({ layout }: { layout?: string }) =>
        layout === INPUT_LAYOUT.stacked &&
        css`
          grid-template-columns: 1fr;
        `}
  `}
`;

const Container = styled.div<{
  layout?: string;
  fieldsetType?: string;
  amountOfItems: number;
  fullWidth?: boolean;
}>`
  ${({ fieldsetType }) => {
    switch (fieldsetType) {
      case InputType.DEFAULT_RADIO:
        return FlexInputContainer;
      case InputType.ENRICHED_RADIO:
        return GridInputContainer;
      case InputType.DEFAULT_CHECKBOX:
        return GridSmallInputContainer;
      case InputType.ENRICHED_CHECKBOX:
        return GridInputContainer;
      case InputType.DEFAULT_TEXT:
        return TextInputContainer;
      default:
    }
  }};
`;
